import type { KeyVal, ProductInformation } from '@dealerportal/common';

interface ContentProximityMillsProps {
  specs: KeyVal[];
  productInfo: ProductInformation;
}

interface Certification {
  value: string;
  iconSrc: string;
  iconAlt: string;
  certificationText: string;
}

const ProductDetailsSection = ({
  id,
  title,
  children,
  className,
  description,
}: {
  id?: string;
  title: string;
  className?: string;
  description?: string;
  children?: React.ReactNode | React.ReactNode[];
}) => {
  return (
    <div id={id} className={className}>
      <h2 className="!ui-text-2xl !ui-m-0">{title}</h2>
      {description && <p className="!ui-text-base !ui-mt-2">{description}</p>}
      {children}
    </div>
  );
};

export const ContentProximityMillsFeatures: React.FC<
  ContentProximityMillsProps
> = (props) => {
  const { specs, productInfo } = props;

  const durabilityRating = findValueByKey(specs, 'Durability');
  const environmentallyFriendlyCertification = findValueByKey(
    specs,
    'EnvironmentallyFriendlyCertification'
  );
  const waterProtection = findValueByKey(specs, 'WaterProtection');
  const petFriendly = findValueByKey(specs, 'PetFriendly');
  const limitedWarrantyResidential = findValueByKey(
    specs,
    'LimitedWarrantyResidential'
  );
  const limitedWarrantyCommercial = findValueByKey(
    specs,
    'LimitedWarrantyCommercial'
  );
  const countryOfOrigin = productInfo.countryOfOrigin;
  const productType = productInfo.productType;
  const wearLayer = findValueByKey(specs, 'LuxuryVinylWearLayer');
  const specialCareRequirements = findValueByKey(
    specs,
    'SpecialCareRequirements'
  );
  const fadeResistanceUVCoating = findValueByKey(
    specs,
    'FadeResistanceUVCoating'
  );
  const moldingType = findValueByKey(specs, 'MoldingType');
  const radiantHeatApproved = findValueByKey(specs, 'RadiantHeatApproved');
  const certifications: Certification[] = [
    {
      value: 'FloorScore',
      iconSrc:
        'https://res.cloudinary.com/force/image/upload/w_auto,h_60,f_auto,q_auto/catalog/FSU-icon-library-white-bg/floorscore.png',
      iconAlt: 'FloorScore',
      certificationText: 'Indoor Air Quality Certification',
    },
    {
      value: 'GreenGuard Gold',
      iconSrc:
        'https://res.cloudinary.com/force/image/upload/w_auto,h_60,f_auto,q_auto/catalog/FSU-icon-library-white-bg/GreenGuard-Gold.png',
      iconAlt: 'GreenGuard Gold',
      certificationText: 'Certified for low chemical emissions',
    },
    {
      value: 'LEED',
      iconSrc:
        'https://res.cloudinary.com/force/image/upload/w_auto,h_60,f_auto,q_auto/catalog/FSU-icon-library-white-bg/leed.png',
      iconAlt: 'LEED',
      certificationText: 'Energy efficiency Certification',
    },
    {
      value: 'GreenGuard',
      iconSrc:
        'https://res.cloudinary.com/force/image/upload/w_auto,h_60,f_auto,q_auto/catalog/FSU-icon-library-white-bg/GreenGuard.png',
      iconAlt: 'GreenGuard',
      certificationText: 'Certified for low chemical emissions',
    },
  ];

  const renderDurabilityRatingIcons = () => {
    const icons = [];
    const durabilityRatingNumber = Number.parseInt(durabilityRating || '0', 10);
    for (let i = 1; i <= 5; i++) {
      if (durabilityRatingNumber === i) {
        icons.push(
          <div
            key={`icon_durability_rating_${i}`}
            className="icon_attribute icon_scores ff_background_center ui-flex"
            id={`icon_durability_rating_${i}`}
          />
        );
      }
    }
    return icons;
  };

  const renderEnvironmentallyFriendlyCertificationIcon = () => {
    const certification = certifications.find(
      (cert) => cert.value === environmentallyFriendlyCertification
    );
    if (certification) {
      const { iconSrc, iconAlt, certificationText } = certification;
      return (
        <div>
          <div className="attribute_icons icon_attribute">
            <img alt={iconAlt} src={iconSrc} className="mr-2" />
          </div>
          <p>{certificationText}</p>
        </div>
      );
    }
    return null;
  };

  const luxuryVinylOrHardwood =
    productType === 'Luxury Vinyl' || productType === 'Hardwood';

  return (
    <>
      <div className="ui-flex ui-flex-wrap ui-gap-4 ui-mt-5">
        {durabilityRating && luxuryVinylOrHardwood && (
          <div>
            {renderDurabilityRatingIcons()}
            <p>Durability Rating: {durabilityRating} out of 5</p>
          </div>
        )}
        {waterProtection === 'Water Resistant' && luxuryVinylOrHardwood && (
          <div className="ui-max-w-[150px]">
            <div
              id="icon_luxury_vinyl_water_protection_water_resistant"
              className="icon_attribute ui-flex"
            />
            <p>{waterProtection}</p>
          </div>
        )}

        {waterProtection && productType === 'Luxury Vinyl' && (
          <div className="ui-max-w-[150px]">
            <div
              id="icon_luxury_vinyl_water_protection_standard"
              className="icon_attribute ui-flex"
            />
            <p>{waterProtection}</p>
          </div>
        )}

        {petFriendly && luxuryVinylOrHardwood && (
          <div className="ui-max-w-[150px]">
            <div className="icon_attribute ui-flex" id="icon_petfriendly" />
            <p>Pet Friendly</p>
          </div>
        )}

        {limitedWarrantyResidential && luxuryVinylOrHardwood && (
          <div className="ui-max-w-[150px]">
            <div
              className="icon_attribute ui-flex"
              id="icon_warranty_residential"
            />
            <p>Residential Warranty: {limitedWarrantyResidential}</p>
          </div>
        )}

        {limitedWarrantyCommercial && luxuryVinylOrHardwood && (
          <div className="ui-max-w-[150px]">
            <div
              className="icon_attribute ui-flex"
              id="icon_warranty_commercial"
            />
            <p>Commercial Warranty: {limitedWarrantyCommercial}</p>
          </div>
        )}

        {countryOfOrigin && luxuryVinylOrHardwood && (
          <div className="ui-max-w-[150px]">
            <div className="icon_attribute ui-flex" id="icon_made_in_usa" />
            <p>Made in {countryOfOrigin}</p>
          </div>
        )}

        {environmentallyFriendlyCertification && luxuryVinylOrHardwood && (
          <div className="ui-max-w-[150px]">
            {renderEnvironmentallyFriendlyCertificationIcon()}
          </div>
        )}

        {wearLayer && luxuryVinylOrHardwood && (
          <div className="ui-max-w-[150px]">
            <div
              id={`icon_wear_layer_${wearLayer.replace(' mil', '')}`}
              className="icon_attribute ui-flex"
            />
            <p>Wear Layer: {wearLayer}</p>
          </div>
        )}

        {specialCareRequirements && luxuryVinylOrHardwood && (
          <div className="ui-max-w-[150px]">
            {specialCareRequirements === 'Caster wheels' && (
              <div
                className="icon_attribute ui-flex"
                id="icon_care_requirement_caster_wheels"
              />
            )}
            {specialCareRequirements === 'Vacuum' && luxuryVinylOrHardwood && (
              <div>
                <div
                  className="icon_attribute ui-flex"
                  id="icon_care_requirement_vacuum"
                />
                <p>Care: {specialCareRequirements}</p>
              </div>
            )}
          </div>
        )}

        {fadeResistanceUVCoating && productType === 'Luxury Vinyl' && (
          <div className="ui-max-w-[150px]">
            <div
              className="icon_attribute ui-flex"
              id="icon_luxury_vinyl_fade_resistance_uv_coating"
            />
            <p>Fade Resistance (UV Coating)</p>
          </div>
        )}

        {radiantHeatApproved && luxuryVinylOrHardwood && (
          <div className="ui-max-w-[150px]">
            <div
              className="icon_attribute ui-flex"
              id="icon_luxury_vinyl_radiant_heat_approved"
            />
            <p>Radiant Heat Approved</p>
          </div>
        )}
      </div>

      {moldingType && luxuryVinylOrHardwood && (
        <>
          <div className="ui-mt-[3rem] ui-mb-[1rem] !ui-max-w-screen-xl">
            <div className="!ui-max-w-screen-xl ui-space-y-16">
              <ProductDetailsSection
                id="pxm-moldings"
                title="Moldings"
                className="ui-text-left"
              />
            </div>
          </div>
          <div
            id="icon_moldings_available_matching"
            className="icons_checkboxes_value"
          >
            <div
              id="icon_moldings_available_t_wrapper"
              className="ff_inline_component"
            >
              <div
                id="icon_moldings_available_t"
                className="icon_attribute ff_background_center"
              />
              <p>T Molding</p>
              <p style={{ display: 'none' }}>
                [[shop:product:attribute:luxury_vinyl_molding_options]]
              </p>
            </div>
            <div
              id="icon_moldings_available_reducer_wrapper"
              className="ff_inline_component"
            >
              <div
                id="icon_moldings_available_reducer"
                className="icon_attribute ff_background_center"
              />
              <p>Reducer</p>
            </div>
            <div
              id="icon_moldings_available_flush_stairnosing_wrapper"
              className="ff_inline_component"
            >
              <div
                id="icon_moldings_available_flush_stairnosing"
                className="icon_attribute ff_background_center"
              />
              <p>Flush Stairnosing</p>
            </div>
            <div
              id="icon_moldings_available_end_cap_wrapper"
              className="ff_inline_component"
            >
              <div
                id="icon_moldings_available_end_cap"
                className="icon_attribute ff_background_center"
              />
              <p>End Cap</p>
            </div>
          </div>
        </>
      )}
    </>
  );
};

// Helper function to find a value by key in the specs array
const findValueByKey = (specs: KeyVal[], key: string): string | undefined => {
  const spec = specs.find((x) => x.key === key);
  return spec?.value;
};
